import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PlayCircle, Youtube } from 'lucide-react';
import { useVideos } from '@/hooks/use-content';
import type { VideoRow as Video } from '@/types/database';
import { getYoutubeEmbedUrl, getYoutubeThumbnailUrl } from '@/lib/youtube';
import SectionEyebrow from './SectionEyebrow';

const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/channel/UC8AnRxepLtQLel8TBFVntvw';

const VideoCard = memo(({ video, onClick }: { video: Video; onClick: () => void }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const thumbnail = getYoutubeThumbnailUrl(video.youtube_url);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative aspect-video overflow-hidden rounded-xl bg-muted text-left hud-frame"
      aria-label={t('videos.playAria', { title: video.title })}
    >
      {isLoading && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
      {thumbnail && (
        <img
          src={thumbnail}
          alt={video.title}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      )}
      <div className="absolute inset-0 bg-background/20 group-hover:bg-background/40 transition-colors flex items-center justify-center">
        <div className="h-14 w-14 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
          <PlayCircle className="h-8 w-8 text-primary" />
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent p-4">
        <span className="text-sm font-semibold text-foreground truncate block">{video.title}</span>
      </div>
    </button>
  );
});
VideoCard.displayName = 'VideoCard';

const VideoLightbox = ({
  video,
  isOpen,
  onClose,
}: {
  video: Video | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  if (!video) return null;
  const embedUrl = getYoutubeEmbedUrl(video.youtube_url);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl w-full max-h-screen overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{video.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={video.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                {t('videos.couldNotLoad')}
              </div>
            )}
          </div>

          {video.description && (
            <p className="text-muted-foreground text-sm leading-relaxed">{video.description}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Videos = () => {
  const { t } = useTranslation();
  const { data: videosData, isLoading } = useVideos();
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const handleClick = (video: Video) => {
    setSelectedVideo(video);
    setIsLightboxOpen(true);
  };

  return (
    <section id="videos" className="py-14 sm:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="06" label={t('videos.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('videos.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            <Trans i18nKey="videos.description" components={{ b: <strong /> }} />
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? Array.from({ length: 3 }).map((_, idx) => <Skeleton key={idx} className="aspect-video rounded-xl" />)
            : videosData?.map((video) => <VideoCard key={video.id} video={video} onClick={() => handleClick(video)} />)}
        </div>

        {!isLoading && (!videosData || videosData.length === 0) && (
          <p className="text-center text-muted-foreground py-8">{t('videos.noVideos')}</p>
        )}

        <div className="text-center mt-10">
          <Button variant="outline" onClick={() => window.open(YOUTUBE_CHANNEL_URL, '_blank')}>
            <Youtube className="mr-2 h-4 w-4" />
            {t('videos.visitChannel')}
          </Button>
        </div>
      </div>

      <VideoLightbox video={selectedVideo} isOpen={isLightboxOpen} onClose={() => setIsLightboxOpen(false)} />
    </section>
  );
};

export default Videos;
